﻿import React, { useEffect } from "react";
import { useState } from "react";
import { Typography, TextField, Grid, Autocomplete } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCustomerCodes,
  selectEditCatalogData,
  selectIsEditMode,
  setCatalogDataField,
  selectAddEditConnectionInfo,
  selectAvailableCustomers,
} from "@/features/connections/manageCatalogDataSlice";
import { defaultCode, blankCode } from "@/features/connections/integrationSlice";
import { AddEditCatalogPopup } from "./CatalogPopup";

const AddShipToPopup = ({ onClose, onValidate, onComplete }) => {
  const dispatch = useDispatch();
  const dataValues = useSelector(selectEditCatalogData);
  const isEdit = useSelector(selectIsEditMode);
  const connectionInfo = useSelector(selectAddEditConnectionInfo);
  const [selectedCustomer, setCustomer] = useState({
    ArtifactDictionaryDataId: dataValues.CustomerId,
    Reference: dataValues.Customer?.CustomerCode ?? "None",
    ArtifactData: JSON.stringify(dataValues.Customer),
  });
  const customerCodes = useSelector(selectAvailableCustomers);
  useEffect(() => {
    if (connectionInfo.connectionId !== -1) {
      dispatch(
        fetchCustomerCodes({
          selectedConnectionId: connectionInfo.connectionId,
        }),
      );
    }
  }, [connectionInfo, dispatch]);

  useEffect(() => {
    if (selectedCustomer.ArtifactDictionaryDataId !== dataValues.CustomerId) {
      setCustomer({
        ArtifactDictionaryDataId: dataValues.CustomerId,
        Reference: dataValues.Customer?.CustomerCode ?? "None",
        ArtifactData: JSON.stringify(dataValues.Customer),
      });
    }
  }, [dataValues]);

  const handleCustomerChange = (newCustomer) => {
    if (
      newCustomer &&
      newCustomer.ArtifactData &&
      newCustomer.Reference !== "None"
    ) {
      dispatch(
        setCatalogDataField({
          name: "Customer",
          value: JSON.parse(newCustomer.ArtifactData),
        }),
      );
    } else {
      dispatch(setCatalogDataField({ name: "Customer", value: null }));
    }
    if (newCustomer && newCustomer.Reference !== "None") {
      dispatch(
        setCatalogDataField({
          name: "CustomerId",
          value: newCustomer.ArtifactDictionaryDataId,
        }),
      );
    } else {
      dispatch(setCatalogDataField({ name: "CustomerId", value: null }));
    }
  };
  const validateForm = () => {
    if (onValidate) {
      const result = onValidate(dataValues);
      if (!result.isValid) return result;
    }
    if (dataValues.ShipToCode === defaultCode) {
      return {
        isValid: false,
        message: `Ship To Code cannot be saved as ${defaultCode}`,
      };
    }
    if (dataValues.ShipToCode === blankCode) {
      return {
        isValid: false,
        message: `Ship To Code cannot be saved as ${blankCode}`,
      };
    }
    if (dataValues.ShipToCode?.includes(",")) {
      return {
        isValid: false,
        message: `The Ship To Code cannot include a ','`,
      };
    }
    if (selectedCustomer == null) {
      return {
        isValid: false,
        message: "Please select an option from the Customer menu.",
      };
    }
    return { isValid: true };
  };

  const handleClose = () => {
    onClose();
    setCustomer({
      ArtifactDictionaryDataId: null,
      Reference: "None",
    });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    dispatch(setCatalogDataField({ name, value }));
  };

  const list = Array.isArray(customerCodes) ? customerCodes : [];
  const options = [{ Reference: "None", CustomerName: "" }, ...list];

  return (
    <AddEditCatalogPopup
      onClose={handleClose}
      onValidate={validateForm}
      onComplete={onComplete}
    >
      <Grid item xs={4}>
        <Typography>
          Customer{" "}
          <Typography
            component="span"
            sx={{ color: "red", fontSize: "1.2rem", fontWeight: "bold" }}
          >
            *
          </Typography>
        </Typography>
        <Autocomplete
          disabled={isEdit}
          options={options}
          getOptionLabel={(option) =>
            option.Reference === "None"
              ? "None"
              : `${option.Reference ?? ""} ${option.CustomerName ?? ""}`
          }
          isOptionEqualToValue={(option, value) =>
            option.Reference === value.Reference
          }
          value={selectedCustomer}
          onChange={(e, newValue) => {
            handleCustomerChange(newValue || null);
          }}
          renderInput={(params) => (
            <TextField
              sx={{ marginTop: 1, marginBottom: 2, maxWidth: 250 }}
              {...params}
              autoComplete="off"
            />
          )}
        />
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To Code </Typography>
        <TextField
          disabled={isEdit}
          variant="outlined"
          name="ShipToCode"
          value={dataValues?.ShipToCode || ""}
          onChange={handleChange}
          sx={{
            width: 250,
            marginTop: 1,
            marginBottom: 2,
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To Name</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="ShipToName"
            value={dataValues?.ShipToName || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To Address 1</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="Address1"
            value={dataValues?.Address1 || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To Address 2</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="Address2"
            value={dataValues?.Address2 || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To Address 3</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="Address3"
            value={dataValues?.Address3 || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To Address 4</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="Address4"
            value={dataValues?.Address4 || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To City</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="City"
            value={dataValues?.City || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To State</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="State"
            value={dataValues?.State || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To ZIP Code</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="PostalCode"
            value={dataValues?.PostalCode || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To Country</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="Country"
            value={dataValues?.Country || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To Phone</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="Phone"
            value={dataValues?.Phone || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To Fax</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="Fax"
            value={dataValues?.Fax || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Ship To Email</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="Email"
            value={dataValues?.Email || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
    </AddEditCatalogPopup>
  );
};

export default AddShipToPopup;
